import React, { useState } from 'react';
import { InvoiceItem } from '../types';
import {
  StyledCard, CardTitle, MaterialItemContainer, InlineDeleteButton,
  DropdownList, DropdownItem,
} from '../cardStyles';
import trash from '../../../../../assets/production-flow/trash.svg';

interface CfopOption {
  id: number;
  code: string;
  description: string;
}

interface NcmOption {
  id: number;
  ncm: string;
}

interface Props {
  items: InvoiceItem[];
  setItems: React.Dispatch<React.SetStateAction<InvoiceItem[]>>;
  cfops: CfopOption[];
  cfopsPerItem: Map<number, CfopOption[]>;
  destStateId: number | null;
  isEmitida: boolean;
  onRemove: (item: { id: number; descricao: string; quantidade: number }) => void;
  fetchNcmsForItem: (item: InvoiceItem) => Promise<void>;
  ncmOptions: NcmOption[];
  onAddItem?: () => void;
  semEmissor?: boolean;
}

const smallInputStyle: React.CSSProperties = {
  border: '1px solid #ddd',
  borderRadius: '100px',
  padding: '4px 8px',
  fontSize: '12px',
  width: '100%',
  textAlign: 'right',
  background: '#fff',
  outline: 'none',
};

const pillInputStyle: React.CSSProperties = {
  ...smallInputStyle,
  background: '#ebebeb',
  borderRadius: '100px',
  textAlign: 'center',
  border: 'none',
};

const GRID_COLS = '2.5fr 1fr 0.6fr 0.7fr 0.5fr 0.6fr 1fr 1.1fr 1fr 1fr 1fr 0.7fr 0.7fr auto';
const HEADERS = [
  'Código / Descrição', 'NCM/SH', 'O/CST', 'CFOP', 'UN',
  'Qtd', 'Vl. Unit.', 'Vl. Total', 'B.Cálc ICMS', 'Vl. ICMS',
  'Vl. IPI', 'Alíq. ICMS%', 'Alíq. IPI%',
];

const ItensSection: React.FC<Props> = ({
  items,
  setItems,
  cfops,
  cfopsPerItem,
  destStateId,
  isEmitida,
  onRemove,
  fetchNcmsForItem,
  ncmOptions,
  onAddItem,
  semEmissor = false,
}) => {
  const [activeCfopDropdown, setActiveCfopDropdown] = useState<number | null>(null);
  const [cfopSearch, setCfopSearch] = useState('');
  const [activeNcmDropdown, setActiveNcmDropdown] = useState<number | null>(null);
  const [ncmSearch, setNcmSearch] = useState('');

  const activeCfopSource =
    activeCfopDropdown !== null ? (cfopsPerItem.get(activeCfopDropdown) || cfops) : cfops;

  const filteredCfops = activeCfopSource.filter((opt) => {
    const q = cfopSearch.toLowerCase();
    return (
      String(opt.code || '').toLowerCase().includes(q) ||
      String(opt.description || '').toLowerCase().includes(q)
    );
  });

  const filteredNcms = ncmOptions.filter((opt) =>
    String(opt.ncm || '').toLowerCase().includes(ncmSearch.toLowerCase())
  );

  return (
    <StyledCard>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <CardTitle style={{ fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>
          Itens da Nota
        </CardTitle>
        {!isEmitida && onAddItem && (
          <button
            onClick={onAddItem}
            style={{
              padding: '6px 14px', borderRadius: '8px', border: 'none',
              background: '#0090D3', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 500,
            }}
          >
            + Adicionar item
          </button>
        )}
      </div>

      {!isEmitida && semEmissor && (
        <div style={{
          fontSize: '12px', color: '#0090D3', marginBottom: '16px',
          display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '15px', height: '15px', borderRadius: '50%',
            border: '1px solid #0090D3', fontSize: '10px', fontStyle: 'italic', fontWeight: 700,
          }}>i</span>
          Selecione a empresa emissora (em Identificação) para habilitar CFOP e NCM.
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: GRID_COLS,
        gap: '8px',
        padding: '0 1.2vw 10px',
        borderBottom: '1px solid #eee',
        marginBottom: '10px',
      }}>
        {HEADERS.map((h, i) => (
          <span key={i} style={{ fontSize: '10px', color: '#999', fontWeight: 'bold', textTransform: 'uppercase' }}>
            {h}
          </span>
        ))}
        <div style={{ width: '28px' }} />
      </div>

      {items.map((item) => (
        <MaterialItemContainer
          key={item.id}
          style={{ border: 'none', background: 'none', padding: '10px 0', margin: 0, gridTemplateColumns: '1fr' }}
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: GRID_COLS,
            gap: '8px',
            alignItems: 'center',
            padding: '0 1.2vw',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <input style={{ fontWeight: 'bold', border: 'none', background: '#f5f5f5', padding: '4px 8px', borderRadius: '4px', fontSize: '11px' }} value={item.codigo || ''} readOnly />
              <input style={{ color: '#666', border: 'none', background: '#f5f5f5', padding: '4px 8px', borderRadius: '4px', fontSize: '10px' }} value={item.descricao || ''} readOnly />
            </div>

            <div style={{ position: 'relative' }}>
              <input
                style={{ ...smallInputStyle, textAlign: 'left' }}
                value={activeNcmDropdown === item.id ? ncmSearch : (item.ncmCode || item.ncm || '')}
                placeholder={semEmissor ? 'Selecione a empresa emissora primeiro' : (item.cfopId && destStateId ? 'Buscar NCM' : 'Defina CFOP e destinatário')}
                disabled={isEmitida || semEmissor || !item.cfopId || !destStateId}
                onChange={(e) => { setNcmSearch(e.target.value); setActiveNcmDropdown(item.id); }}
                onFocus={() => { setNcmSearch(''); setActiveNcmDropdown(item.id); fetchNcmsForItem(item); }}
                onBlur={() => setTimeout(() => setActiveNcmDropdown(null), 200)}
                title="Selecionar NCM com cenário fiscal"
              />
              {activeNcmDropdown === item.id && filteredNcms.length > 0 && (
                <DropdownList style={{ top: 'calc(100% + 4px)', zIndex: 9999, borderRadius: '8px', minWidth: '220px' }}>
                  {filteredNcms.map((n) => (
                    <DropdownItem
                      key={n.id}
                      onMouseDown={() => {
                        setItems((prev) =>
                          prev.map((p) =>
                            p.id === item.id ? { ...p, ncmId: n.id, ncmCode: n.ncm, ncm: n.ncm } : p
                          )
                        );
                        setActiveNcmDropdown(null);
                      }}
                    >
                      <span style={{ fontSize: '11px', color: '#333' }}>{n.ncm}</span>
                    </DropdownItem>
                  ))}
                </DropdownList>
              )}
            </div>

            <input style={pillInputStyle} value={item.cst || '000'} readOnly />

            <div style={{ position: 'relative' }}>
              <input
                style={pillInputStyle}
                value={activeCfopDropdown === item.id ? cfopSearch : (item.cfop || '')}
                placeholder={semEmissor ? 'Selecione a empresa emissora primeiro' : 'Buscar CFOP'}
                disabled={isEmitida || semEmissor}
                onChange={(e) => { setCfopSearch(e.target.value); setActiveCfopDropdown(item.id); }}
                onFocus={() => { setCfopSearch(''); setActiveCfopDropdown(item.id); }}
                onBlur={() => setTimeout(() => setActiveCfopDropdown(null), 200)}
                title="Editar CFOP manualmente"
              />
              {activeCfopDropdown === item.id && filteredCfops.length > 0 && (
                <DropdownList style={{ top: 'calc(100% + 4px)', zIndex: 9999, borderRadius: '8px', minWidth: '300px' }}>
                  {filteredCfops.map((cfop) => (
                    <DropdownItem
                      key={cfop.id}
                      onMouseDown={() => {
                        setItems((prev) =>
                          prev.map((p) =>
                            p.id === item.id ? { ...p, cfopId: cfop.id, cfop: cfop.code.toString() } : p
                          )
                        );
                        setActiveCfopDropdown(null);
                      }}
                    >
                      <span style={{ fontSize: '11px', color: '#333' }}>{cfop.code} — {cfop.description}</span>
                    </DropdownItem>
                  ))}
                </DropdownList>
              )}
            </div>

            <input style={{ ...smallInputStyle, textAlign: 'center' }} value={item.unidade || ''} readOnly />
            <input style={{ ...pillInputStyle, fontWeight: 'bold' }} value={item.quantidade || ''} readOnly />
            <input style={smallInputStyle} value={item.vlUnit || ''} readOnly />
            <input style={{ ...smallInputStyle, fontWeight: 'bold' }} value={item.vlTotal || ''} readOnly />
            <input style={smallInputStyle} value={item.bcIcms || ''} readOnly />
            <input style={smallInputStyle} value={item.vlIcms || ''} readOnly />
            <input style={smallInputStyle} value={item.vlIpi || ''} readOnly />
            <input style={pillInputStyle} value={item.aliqIcms || '0'} readOnly />
            <input style={pillInputStyle} value={item.aliqIpi || '0'} readOnly />

            {!isEmitida && (
              <InlineDeleteButton
                onClick={() => onRemove({ id: Number(item.id), descricao: item.descricao, quantidade: Number(item.quantidade) })}
                style={{ marginTop: 0, background: '#feecec', borderRadius: '4px', width: '28px', height: '28px', minWidth: 'auto' }}
              >
                <img src={trash} alt="Excluir" style={{ width: '14px', filter: 'invert(37%) sepia(93%) saturate(3661%) hue-rotate(338deg) brightness(91%) contrast(107%)' }} />
              </InlineDeleteButton>
            )}
            {isEmitida && <div style={{ width: '28px' }} />}
          </div>

          <div style={{ display: 'flex', gap: '16px', padding: '6px 1.2vw 0', fontSize: '10px', color: '#888', flexWrap: 'wrap' }}>
            <span>CST PIS/COFINS: <strong>{item.cstPisCofins || '—'}</strong></span>
            <span>CST IBS/CBS: <strong>{item.cstIbsCbs || '—'}</strong></span>
            <span>Classif. trib.: <strong>{item.classificacao || '—'}</strong></span>
            <span>Vl. IBS: <strong>{item.vlIbs || '—'}</strong></span>
            <span>Vl. CBS: <strong>{item.vlCbs || '—'}</strong></span>
          </div>
        </MaterialItemContainer>
      ))}
    </StyledCard>
  );
};

export default ItensSection;
